// Centralized runtime configuration. Reads from window.__APP_CONFIG__ (if present)
// and REACT_APP_* environment variables. Avoids hardcoded values in components.

export interface AppConfig {
  /** Origin only (e.g. https://app.example.com). Do not append `/api` — request paths already include `/api/v1/...`. */
  apiBaseUrl: string;
  brandName: string;
  companyName: string;
  primaryColor: string;
  /** URL for the app favicon shown in the browser tab and pre-auth UI (e.g. /custom-favicon.ico or https://…). */
  faviconUrl: string;
  /** URL for the default logo image used in pre-auth UI and the branding admin page fallback. */
  logoUrl: string;
  privacyUrl?: string;
  termsUrl?: string;
  googleApiKey?: string;
  recaptchaSiteKey?: string;
}

declare global {
  interface Window {
    __APP_CONFIG__?: Partial<AppConfig>;
  }
}

function readEnv(name: string): string | undefined {
  // CRA exposes REACT_APP_* at build time
  // eslint-disable-next-line no-restricted-globals
  return (process.env as any)?.[name] as string | undefined;
}

export const config: AppConfig = {
  apiBaseUrl:
    (typeof window !== 'undefined' && window.__APP_CONFIG__?.apiBaseUrl) ||
    readEnv('REACT_APP_API_URL') ||
    'http://localhost:8000',
  brandName:
    (typeof window !== 'undefined' && window.__APP_CONFIG__?.brandName) ||
    readEnv('REACT_APP_BRAND_NAME') ||
    'Klaris AI',
  companyName:
    (typeof window !== 'undefined' && window.__APP_CONFIG__?.companyName) ||
    readEnv('REACT_APP_COMPANY_NAME') ||
    'Klaris Labs',
  primaryColor:
    (typeof window !== 'undefined' && window.__APP_CONFIG__?.primaryColor) ||
    readEnv('REACT_APP_PRIMARY_COLOR') ||
    '#1976d2',
  faviconUrl:
    (typeof window !== 'undefined' && window.__APP_CONFIG__?.faviconUrl) ||
    readEnv('REACT_APP_FAVICON_URL') ||
    '/klaris-favicon.ico',
  logoUrl:
    (typeof window !== 'undefined' && window.__APP_CONFIG__?.logoUrl) ||
    readEnv('REACT_APP_LOGO_URL') ||
    '/klaris-logo.svg',
  privacyUrl:
    (typeof window !== 'undefined' && window.__APP_CONFIG__?.privacyUrl) ||
    readEnv('REACT_APP_PRIVACY_URL') ||
    'https://tryklaris.ai/privacy',
  termsUrl:
    (typeof window !== 'undefined' && window.__APP_CONFIG__?.termsUrl) ||
    readEnv('REACT_APP_TERMS_URL') ||
    'https://tryklaris.ai/terms',
  googleApiKey:
    (typeof window !== 'undefined' && window.__APP_CONFIG__?.googleApiKey) ||
    readEnv('REACT_APP_GOOGLE_API_KEY') ||
    undefined,
  recaptchaSiteKey:
    (typeof window !== 'undefined' && window.__APP_CONFIG__?.recaptchaSiteKey) ||
    readEnv('REACT_APP_RECAPTCHA_SITE_KEY') ||
    undefined,
};

export function buildApiUrl(path: string): string {
  const base = (config.apiBaseUrl || '').replace(/\/+$/, '');
  if (!path) return base;
  const suffix = path.startsWith('/') ? path : `/${path}`;
  // Collapse accidental double slashes but keep protocol intact
  return `${base}${suffix}`.replace(/([^:]\/)\/+/, '$1');
}

/** Prefix for tenant-scoped APIs, e.g. `/api/v1/tenants/{tenantId}`. */
export function tenantApiPrefix(tenantId: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantId)}`;
}

